// Timecode parsing + formatting for media (audio/video) time ranges.
//
// One place for the seconds⇄string conversion the redaction editor and the
// excerpt studio need, so manual timestamp entry and the waveform agree on
// the same format. Display is m:ss.d, or h:mm:ss.d once the position is an
// hour or more (hearings run for hours); parsing is lenient and accepts raw
// seconds, m:ss, m:ss.d, or h:mm:ss(.d).

/**
 * Parse a user-typed timecode to seconds (float), or null if unparseable.
 * Accepts: "83.5" (seconds), "1:23", "1:23.5" (m:ss), "1:02:03" (h:mm:ss).
 */
export function parseTimecode(input) {
  if (input == null) return null;
  const s = String(input).trim();
  if (!s) return null;
  if (s.includes(':')) {
    const parts = s.split(':');
    if (parts.length > 3) return null;
    let total = 0;
    for (const p of parts) {
      if (p === '' || Number.isNaN(Number(p))) return null;
      total = total * 60 + Number(p);
    }
    return Number.isFinite(total) && total >= 0 ? total : null;
  }
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Format seconds as m:ss.d, or h:mm:ss.d from one hour up (default 1 decimal).
 * "" for non-finite.
 */
export function formatTimecode(seconds, { decimals = 1 } = {}) {
  if (!Number.isFinite(seconds)) return '';
  const sign = seconds < 0 ? '-' : '';
  const abs = Math.abs(seconds);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const pad = decimals > 0 ? 3 + decimals : 2; // "05.5" / "05"
  const sec = (abs % 60).toFixed(decimals).padStart(pad, '0');
  if (h > 0) return `${sign}${h}:${String(m).padStart(2, '0')}:${sec}`;
  return `${sign}${m}:${sec}`;
}

/** Whole-second clock: m:ss, or h:mm:ss from one hour up. "" for non-finite. */
export function formatClock(seconds) {
  if (!Number.isFinite(seconds)) return '';
  return formatTimecode(Math.floor(Math.abs(seconds)), { decimals: 0 });
}

/** "3 min 20 s" / "1 h 02 min" / "42 s" — a length in words for cost lines. */
export function formatDurationWords(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h} h ${String(m).padStart(2, '0')} min`;
  if (m > 0) return s ? `${m} min ${s} s` : `${m} min`;
  return `${s} s`;
}

/** Round to the 3-decimal precision the redaction backend stores. */
export function round3(n) {
  return Math.round(Number(n) * 1000) / 1000;
}
